import React, { forwardRef, ReactNode, HTMLAttributes } from "react";
import { cn } from "../../styles";

export interface PlaceholderProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Content to display inside the placeholder.
   * Can be text, icons, or any React elements.
   */
  children?: ReactNode;
}

/**
 * Placeholder component for displaying empty states, loading areas, or content scaffolding.
 * Provides a visually distinct gray box with centered content that can be used as a
 * temporary stand-in for actual content during development or loading states.
 *
 * The component forwards refs to the underlying div element and accepts all standard
 * HTML div attributes, making it fully compatible with form libraries and DOM manipulation.
 */
export const Placeholder = forwardRef<HTMLDivElement, PlaceholderProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          "m-0 bg-gray-200 self-stretch border-2 border-gray-300 text-gray-400 flex justify-center items-center rounded-sm p-4",
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Placeholder.displayName = "Placeholder";
